import { Avatar } from "@mui/material";
import { getInitials } from "helpers/utils";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import type { ITransferee } from "store/transfereesStore";

import variables from "../../assets/styles/_variables.module.scss";
import LoadingDots from "components/LoadingDots";
import TextSkeleton from "../shared/TextSkeleton";
import styles from "./style.module.scss";

interface IListItem {
    /**
     * Data of the transferee.
     */
    transferee?: ITransferee;
    /**
     * Triggers selecting item.
     * @param transferee Data of the transferee
     */
    action?: (transferee: ITransferee) => void;
    /**
     * Needed for edit mode to add opacity to items not selected.
     */
    disabled?: boolean;
    /**
     * Is item the one selected.
     */
    active?: boolean;
    isLoading?: boolean;
    skeletonIndex?: number;
}
/**
 * Block with transferee name on Element List.
 */
const TransfereeListItem = forwardRef<HTMLDivElement, IListItem>(function TransfereeListItem(
    { transferee, action, disabled = false, active = false, isLoading = false, skeletonIndex = 0 }: IListItem,
    ref,
) {
    const [t] = useTranslation();
    return (
        <div
            className={`${styles["transferee-list-item"]}${active ? ` ${styles["active"]}` : ""}${
                isLoading ? ` ${styles["transferee-list-item--loading"]}` : ""
            }`}
            onClick={isLoading || !action || !transferee ? undefined : () => action(transferee)}
            style={disabled ? { opacity: 0.1, pointerEvents: "none" } : {}}
            ref={ref}
        >
            <div className={styles["avatar-block"]}>
                {isLoading ? (
                    <TextSkeleton variant="circular" width={40} height={40} />
                ) : (
                    <Avatar
                        src={transferee?.avatarUrl}
                        sx={{
                            backgroundColor: "rgba(184, 174, 233, 0.2)",
                            color: variables["foreground"],
                            fontSize: 14,
                        }}
                        imgProps={{ sx: { objectFit: "cover" } }}
                    >
                        {getInitials((transferee?.name ?? "").toUpperCase())}
                    </Avatar>
                )}
            </div>
            <div className={styles["transferee-data"]}>
                <span className={styles["transferee-name"]}>
                    {isLoading ? (
                        <TextSkeleton width={`${8 + (skeletonIndex % 4)}em`} />
                    ) : (
                        (transferee?.name ?? "").toUpperCase()
                    )}
                </span>
                <span className={styles["transferee-count"]}>
                    {isLoading ? (
                        <TextSkeleton width={`${4 + (skeletonIndex % 3)}em`} />
                    ) : transferee?.transferTermCount == null ? (
                        <LoadingDots compact />
                    ) : (
                        <>
                            {transferee.transferTermCount} {t("TransferTerms.Contracts").toLowerCase()}
                        </>
                    )}
                </span>
            </div>
        </div>
    );
});

export default TransfereeListItem;
